<template>
  <div>
    <section class="py-15 bg-neutral-800 relative overflow-hidden lg:py-24">
      <OSvg svg="large-x-logo" class="absolute hidden lg:block large-x-logo pointer-events-none" />
      <div class="lg-container-x lg:flex lg:flex-row-reverse">
        <div class="lg:flex-1">
          <div class="flex justify-center items-center relative md:flex-1">
            <OSvg svg="medium-x-logo" class="absolute lg:hidden medium-x-logo pointer-events-none" />
            <div class="inline-flex justify-center pb-15 relative md:pb-0">
              <OImage v-bind="productImage" img-class="pointer-events-none product-image" />
              <div class="absolute spans-container">
                <span class="price-span" v-text="formattedPrice" />
                <span class="free-span" v-text="$t('SignUp.Free')" />
              </div>
            </div>
          </div>
        </div>
        <div class="text-white text-center lg:text-left lg:pt-7">
          <div class="max-w-600">
            <p class="mb-rem ota-p14 px-3" v-text="getTranslation('HeroSection.Description')" />
            <h1 class="o-heading-1 mb-6 px-3" v-text="getTranslation('HeroSection.Title')" />
            <div class="text-center lg:text-left">
              <NewButton tag="a" target="_blank" rel="noopener noreferrer" :href="toLink" @click="pushToDataLayer({ event: 'get_started_clicked' })">
                {{ getTranslation('CTA.GetStarted') }}
              </NewButton>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="cta-section py-15 text-neutral-1000 padding-y">
      <div class="lg-container-x lg:grid lg:grid-cols-2">
        <div class="px-4">
          <div class="max-w-600 mx-auto lg:px-5">
            <h3 class="o-heading-2 mb-5 lg:mb-10" v-text="getTranslation('WhyJoinUs.Title')" />
            <p class="o-body mb-5 lg:mb-5" v-html="getTranslation('WhyJoinUs.Description')" />
          </div>
        </div>
        <div class="px-4 lg:flex lg:justify-center">
          <OSvg svg="ota-world-map" />
        </div>
      </div>
    </section>
    <section class="cta-section bg-neutral-800 py-15 px-4 text-center text-neutral-00 padding-y">
      <div class="max-w-600">
        <p class="o-heading-2 mb-rem lg:mb-5" v-text="getTranslation('SupportAffiliates.Title')" />
        <p class="o-body mb-5 lg:mb-5" v-text="getTranslation('SupportAffiliates.Description')" />
        <NewButton tag="a" target="_blank" rel="noopener noreferrer" :href="toLink" @click="pushToDataLayer({ event: 'contact_us_clicked' })">
          {{ getTranslation('CTA.Contact') }}
        </NewButton>
      </div>
    </section>
    <section class="cta-section py-15 padding-y text-neutral-1000">
      <div class="lg-container-x">
        <div class="px-4">
          <h3 class="o-heading-2 mb-5 lg:mb-16" v-text="getTranslation('WhyAffiliatesLoveUs.Title')" />
        </div>
        <div class="px-4 lg:grid lg:grid-cols-2 lg:gap-12">
          <ul class="pt-3 lg:pt-0 item-list">
            <li v-for="(item, index) in firstHalfItems" :key="index" class="flex mb-5 items-stretch">
              <div class="flex items-start">
                <div class="p-2 flex-grow-0 flex items-center justify-center mr-5 rounded-full bg-newGreen">
                  <OSvg class="fill-neutral-00 w-5 h-5" svg="checkmark" />
                </div>
              </div>
              <span class="o-body font-display text-xl" v-html="item" />
            </li>
          </ul>
          <ul class="item-list">
            <li v-for="(item, index) in secondHalfItems" :key="index" class="flex mb-5 items-stretch">
              <div class="flex items-start">
                <div class="p-2 flex-grow-0 flex items-center justify-center mr-5 rounded-full bg-newGreen">
                  <OSvg class="fill-neutral-00 w-5 h-5" svg="checkmark" />
                </div>
              </div>
              <span class="o-body font-display text-xl" v-html="item" />
            </li>
          </ul>
        </div>
      </div>
    </section>
    <section class="bg-women-shade py-15 px-4 text-neutral-1000 padding-y">
      <div class="lg-container-x lg:grid lg:grid-cols-3 gap-5 lg:gap-9">
        <div v-for="(item, svgIndex) in svgItems" :key="svgIndex" class="svg-indexes relative mb-5 lg:mb-0">
          <div class="svg-indexes-index absolute flex-shrink-0 w-7 h-7 rounded-full font-display text-xl flex justify-center items-center text-green bg-yellow-400 mr-3">
            <span class="font-body text-xl font-bold text-gray-600" v-text="svgIndex + 1" />
          </div>
          <div
            :class="['rounded-rem w-full flex overflow-hidden h-full', {
              'border-green-300 border-2': svgIndex === 3
            }]"
          >
            <div
              :class="['flex justify-center items-center p-5 lg:p-7 svg-indexes-icon-container', {
                'bg-neutral-800': svgIndex !== 3,
                'bg-green-300': svgIndex === 3
              }]"
            >
              <OSvg :svg="item.icon" class="fill-white" />
            </div>
            <div class="px-3 flex justify-center items-center bg-white flex-1">
              <span class="o-body" v-html="item.text" />
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="usps-section py-15 padding-y px-4 lg:my-12">
      <div class="lg-container-x lg:flex">
        <div>
          <div class="max-w-600 lg:px-12">
            <p class="o-heading-2 mb-5 lg:mb-6" v-text="getTranslation('WhatKindOfPartners.Title')" />
            <p class="ota-p14" v-html="getTranslation('WhatKindOfPartners.Description')" />
          </div>
        </div>
        <div class="lg:flex-1 pt-10 flex justify-center lg:justify-end lg:pt-0">
          <SimpleSlider :images="sliderImages" class="max-w-1000px" />
        </div>
      </div>
    </section>
    <section class="usps-section relative py-15 padding-y bg-neutral-800 cta-section text-neutral-00">
      <div class="lg-container-x lg:flex">
        <div class="px-4 usps-section-left lg:pr-7">
          <h2 class="o-heading-2 mb-rem lg:mb-5" v-text="getTranslation('GetStartedBulletPoints.Title')" />
          <p class="o-body mb-5 lg:mb-5" v-html="getTranslation('GetStartedBulletPoints.Description')" />
          <div class="mb-4">
            <NewButton tag="a" target="_blank" rel="noopener noreferrer" :href="toLink" @click="pushToDataLayer({ event: 'get_started_clicked' })">
              {{ getTranslation('CTA.GetStarted') }}
            </NewButton>
          </div>
        </div>
        <div class="px-4 usps-section-right">
          <ul class="py-12 block">
            <li v-for="(step, number) in stepsToShow" :key="step" class="mb-7">
              <div class="flex">
                <div class="flex-shrink-0 w-7 h-7 rounded-full font-display text-xl flex justify-center items-center text-green bg-yellow-400 mr-3">
                  <span class="font-body text-xl font-bold text-gray-600" v-text="number + 1" />
                </div>
                <span class="o-body font-bold font-display text-xl" v-text="step" />
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>
    <section class="bg-women-shade py-15 px-4 text-center text-neutral-1000 padding-y lg-negative-margin">
      <div class="hidden lg:block lg:h-56" />
      <div class="max-w-600">
        <p class="o-heading-2 mb-rem lg:mb-5" v-text="getTranslation('JoinSection.Title')" />
        <p class="o-body mb-7 lg:mb-5" v-text="getTranslation('JoinSection.Description')" />
        <div class="lg:flex lg:justify-center">
          <div class="mb-rem lg:mr-3 lg:mb-0">
            <NewButton tag="a" target="_blank" rel="noopener noreferrer" :href="toLink" @click="pushToDataLayer({ event: 'get_started_clicked' })">
              {{ getTranslation('CTA.GetStarted') }}
            </NewButton>
          </div>
          <div>
            <NewButton
              tag="a"
              target="_blank"
              rel="noopener noreferrer"
              :href="programSummaryLink"
              variant="outline"
              @click="pushToDataLayer({ event: 'get_program_summary_clicked' })"
            >
              {{ getTranslation('CTA.GetProgramSummary') }}
            </NewButton>
          </div>
        </div>
      </div>
    </section>
    <section>
      <StarRating
        v-if="rating"
        class="pt-10 px-5 pb-32 md:pt-20 md:pb-40"
        :title="'TryItNow.ReviewsTitle'"
        :rating="rating"
        :reviews="ratingTotal"
        @click="onRatingClick"
      />
    </section>
  </div>
</template>

<script>
import { GET_GOOGLE_RATING } from '@/utils/store/actionTypes'
import { mapActions, mapState } from 'vuex'
import NewButton from '@/components/NewButton'
import OImage from '@/components/OImage'
import OSvg from '@/components/OSvg'
import SimpleSlider from '@/components/SimpleSlider'
import { addLocalCurrency, convertSrc, getImagePaths } from '@/utils/general'
import { Currencies } from '@/enums/general'
import { MenPrices } from '@/enums/products'
import textVariablesMixin from '@/mixins/textVariables'
import { pushToDataLayer } from '@/utils/gtm'

export default {
  name: 'Affiliate',
  layout: 'homepage',
  components: {
    NewButton,
    OSvg,
    OImage,
    SimpleSlider
  },
  mixins: [textVariablesMixin],
  async fetch () {
    await this.getAndSetGoogleRating()
  },
  data () {
    return {
      rating: null,
      ratingTotal: null
    }
  },
  computed: {
    sliderImages () {
      return [
        getImagePaths({ path: '/images/affiliate-banners/vita', s3: true }),
        getImagePaths({ path: '/images/affiliate-banners/vice', s3: true }),
        getImagePaths({ path: '/images/affiliate-banners/ruby', s3: true }),
        getImagePaths({ path: '/images/affiliate-banners/tyle', s3: true })
      ]
    },
    toLink () {
      return 'https://onthatassflow.everflowclient.io/affiliate/signup'
    },
    programSummaryLink () {
      return convertSrc('/images/frontend/affiliate/affiliate-program.pdf', true)
    },
    formattedPrice () {
      const countryCode = `${this.locale}-${this.country}`

      return addLocalCurrency(MenPrices[this.country], countryCode, Currencies[this.country])
    },
    productImage () {
      return getImagePaths({
        path: '/images/design/boriky/products/boriky-men-originals-boxershort',
        s3: true,
        noMobile: true
      })
    },
    stepsToShow () {
      const path = `${this.locizePath}.GetStartedBulletPoints.Steps`
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.getTranslation(`GetStartedBulletPoints.Steps[${i}]`))
    },
    svgItems () {
      const path = `${this.locizePath}.IconSection.Items`
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      const icons = [
        {
          icon: 'noun-view'
        },
        {
          icon: 'noun-click'
        },
        {
          icon: 'noun-pant'
        },
        {
          icon: 'noun-coin'
        },
        {
          icon: 'noun-calender'
        },
        {
          icon: 'noun-membership'
        }
      ]

      return items.map((_, i) => ({
        icon: icons[i].icon,
        text: this.getTranslation(`IconSection.Items[${i}]`)
      }))
    },
    variables () {
      return {
        ...this.textVariables,
        affiliatesLink: convertSrc('/images/frontend/static/affiliates-signup.webp', true)
      }
    },
    items () {
      const path = `${this.locizePath}.WhyAffiliatesLoveUs.Items`
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.getTranslation(`WhyAffiliatesLoveUs.Items[${i}]`))
    },
    firstHalfItems () {
      const halfLength = Math.ceil(this.items.length / 2)
      return this.items.slice(0, halfLength)
    },
    secondHalfItems () {
      const halfLength = Math.ceil(this.items.length / 2)
      return this.items.slice(halfLength)
    },
    locizePath () {
      return 'LandingsPage.AffiliatePage'
    },
    ...mapState('app', {
      country: state => state.country,
      locale: state => state.locale
    })
  },
  methods: {
    async getAndSetGoogleRating () {
      try {
        const { rating, ratingTotal } = await this.getGoogleRating()

        this.rating = rating
        this.ratingTotal = ratingTotal
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    getTranslation (key) {
      return this.$t(`${this.locizePath}.${key}`, this.variables)
    },
    pushToDataLayer ({ event = 'get_started_clicked' } = {}) {
      pushToDataLayer({
        event
      })
    },
    ...mapActions('homepage', {
      getGoogleRating: GET_GOOGLE_RATING
    })
  },
  head () {
    return {
      title: this.getTranslation('Meta.Title'),
      meta: [
        { hid: 'description', name: 'description', content: this.getTranslation('Meta.Description') },
        { hid: 'og:title', name: 'og:title', property: 'og:title', content: this.getTranslation('Meta.Title') },
        { hid: 'og:description', name: 'og:description', property: 'og:description', content: this.getTranslation('Meta.Description') },
        { hid: 'og:image', name: 'og:image', property: 'og:image', content: 'https://onthatass.com/images/design/ursa/campaign/ursa-men-originals.webp' }
      ]
    }
  }
}
</script>

<style lang="postcss" scoped>
.bg-newGreen {
  background-color: #34A853;
}

.max-w-1000px {
  max-width: 42.8rem;
}

.medium-x-logo {
  right: 0rem;
  top: -11rem;
}

.svg-indexes {
  &-index {
    top: -1.5rem;
    left: 1rem;
  }

  &-icon-container {
    @apply flex items-center justify-center;
    min-width: 14rem;
    min-height: 14rem;
  }
}

.spans-container {
  right: -12px;
  top: 58px;
}

.price-span {
  @apply block bg-white absolute;
  padding-top: 6.09px;
  padding-right: 7.97px;
  padding-bottom: 6.74px;
  padding-left: 7.97px;

  font-family: Montserrat;
  font-weight: 800;
  font-size: 22.99px;
  line-height: 100%;
  text-align: center;
  text-transform: uppercase;
  transform: rotate(6.9deg);
  position: relative;
  overflow: hidden;
}

.free-span {
  @apply block absolute;
  background-color: rgba(127, 255, 0, 1);
  padding-top: 6.09px;
  padding-right: 7.97px;
  padding-bottom: 6.74px;
  padding-left: 7.97px;

  font-family: Montserrat;
  font-weight: 800;
  font-size: 22.99px;
  line-height: 100%;
  text-align: center;
  text-transform: uppercase;
  transform: rotate(-9.82deg);
  top: 30px;
}

>>> .slick-slide img, >>> .slick-slide {
  border-radius: .8rem;
}

.cta-section {
  >>> ul {
    @apply px-3;
  }
}

.price-span::after {
  content: '';
  position: absolute;
  left: 0;
  top: 17px;
  width: 85%;
  height: 3px;
  background: black;
  transform: rotate(-10deg) translate(12%, 0);
  pointer-events: none;
}

@screen md {
  .price-span, .free-span {
    font-size: 3.4rem;
  }

  .free-span {
    top: 6rem;
  }

  .price-span {
    top: 2rem;
  }

  .price-span::after {
    content: '';
    position: absolute;
    left: 0;
    top: 22px;
    width: 90%;
    height: 4px;
    background: black;
    transform: rotate(-10deg) translate(7%, 0);
    pointer-events: none;
  }
}

@screen lg {
  .cta-section {
    box-sizing: content-box;
    @apply rounded-rem mx-9;
  }

  .svg-indexes {
    &-icon-container {
      min-width: 16rem;
      min-height: 16rem;
    }
  }

  .large-x-logo {
    top: 0;
    right: -13rem;
  }

  .max-w-600 {
    @apply mx-auto;
    max-width: 60rem;
  }

  .padding-y {
    padding-top: 10rem;
    padding-bottom: 10rem;
  }

  .lg-container-x {
    max-width: 110rem;
    margin-left: auto;
    margin-right: auto;
  }

  .lg-negative-margin {
    margin-top: -20rem;
  }

  .usps-section {
    &-left {
      flex: 40%;
    }

    &-right {
      flex: 60%;
    }
  }
}
</style>
